import type * as echarts from 'echarts';

/**
 * Registro de las instancias de echarts vivas y de cuándo terminó de pintar cada una.
 *
 * Existe para que el export del tablero (`useExportMetricsView`) sepa cuándo la grilla terminó
 * de re-renderizar y recién entonces rasterizarla; si no, se capturan widgets a medio pintar.
 *
 * La suscripción a `finished` la hace `ECharts.tsx` en el mismo effect del `init` y **antes** del
 * primer `setOption`: echarts emite ese evento a los pocos milisegundos de pintar, así que
 * suscribirse más tarde —por ejemplo al apretar el botón de exportar— es suscribirse cuando el
 * evento ya pasó, y la espera nunca resuelve.
 */
const instances = new Map<echarts.ECharts, { lastFinishedAt: number }>();

export function registerChart(instance: echarts.ECharts): void {
  instances.set(instance, { lastFinishedAt: 0 });
}

export function unregisterChart(instance: echarts.ECharts): void {
  instances.delete(instance);
}

export function markChartFinished(instance: echarts.ECharts): void {
  const entry = instances.get(instance);
  // Puede llegar un 'finished' tardío de una instancia ya desregistrada: se ignora.
  if (entry == null) return;
  entry.lastFinishedAt = Date.now();
}

/** `true` si todas las instancias vivas terminaron de pintar después de `timestamp`. */
export function allChartsIdleSince(timestamp: number): boolean {
  for (const { lastFinishedAt } of instances.values()) {
    if (lastFinishedAt < timestamp) return false;
  }
  return true;
}

/**
 * Fuerza un `resize()` en todas las instancias vivas.
 *
 * El export lo usa después de fijar el ancho de captura, por dos razones: alinea los charts al
 * ancho nuevo, y garantiza que cada instancia vuelva a emitir `finished`. Sin ese re-render
 * forzado, un tablero que ya estaba en estado estable no emitiría nada nuevo y la espera de
 * readiness se comería el timeout completo.
 */
export function resizeAllCharts(): void {
  for (const instance of instances.keys()) {
    instance.resize();
  }
}

export function liveChartCount(): number {
  return instances.size;
}
